import { Card, Checkbox, Col, Form, InputNumber, Row, Select } from "antd";
import K from "utilities/constants";
import { AmountTypeField } from "./amountTypeField";
import styles from "./recursiveComponent.module.scss";
import RepetitiveMonthDayAlert from "./repetitiveMonthDayAlert";

const { RecurringType } = K;
const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const options = Array.from({ length: 31 }).map((_, i) => ({
  label: i + 1,
  value: i + 1,
}));

export default function RecursiveComponent({
  fieldObj,
  parentIndex,
  currencyList,
}) {
  const formItemName =
    parentIndex !== undefined ? [parentIndex, fieldObj.id] : [fieldObj.id];

  const recurringTypePath =
    parentIndex !== undefined
      ? ["jobLevelBillingRules", parentIndex, fieldObj.id, "recurringType"]
      : [fieldObj.id, "recurringType"];

  const shouldUpdate = (prevValues, currValues) => {
    if (parentIndex !== undefined) {
      return (
        prevValues.jobLevelBillingRules[parentIndex][fieldObj.id]
          .recurringType !==
        currValues.jobLevelBillingRules[parentIndex][fieldObj.id].recurringType
      );
    }
    return (
      prevValues[fieldObj.id].recurringType !==
      currValues[fieldObj.id].recurringType
    );
  };

  const recurringComponent = {
    [RecurringType.Week]: (
      <Col span={24}>
        <Form.Item
          name={[...formItemName, "selectedDays"]}
          rules={[
            {
              required: true,
              message: "Please select week day!",
            },
          ]}
        >
          <Checkbox.Group
            className={`recurringCheckboxGroup ${styles.recurringCheckboxGroup}`}
          >
            {days.map((checkbox, i) => (
              <Checkbox className={styles.recurringCheckbox} key={i} value={i}>
                {checkbox}
              </Checkbox>
            ))}
          </Checkbox.Group>
        </Form.Item>
      </Col>
    ),
    [RecurringType.Month]: (
      <Col xs={24} md={12}>
        <Form.Item
          className={styles.formItemDate}
          label="On this date"
          name={[...formItemName, "repetitiveMonthDay"]}
          rules={[
            {
              required: true,
              message: "Please select month day!",
            },
            {
              pattern: new RegExp("^[0-9]*$", "g"),
              message: "Decimal values are not allowed!",
            },
          ]}
        >
          <Select
            showSearch
            className={styles.recurringDateSelect}
            placeholder="Select"
            optionFilterProp="label"
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
            options={options}
          />
        </Form.Item>
      </Col>
    ),
  };

  return (
    <Card title={fieldObj.name} className={styles.fieldSet}>
      <Row align="bottom" gutter={[12, 0]}>
        <Col xs={24} md={12} className={styles.recursiveAmount}>
          <Form.Item
            className={styles.formItemRepeat}
            name={[...formItemName, "repetitiveQuantity"]}
            dependencies={[recurringTypePath]}
            label="Repeat every"
            rules={[
              { required: true, message: "Enter repeat every value!" },
              {
                pattern: new RegExp("^[0-9]*$", "g"),
                message: "Decimal values are not allowed!",
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const recurringType = getFieldValue(recurringTypePath);
                  const max =
                    recurringType === RecurringType.Week ? 52 : 12;
                  if (value !== null && value !== undefined && value > max) {
                    return Promise.reject(
                      new Error(`Maximum value is ${max}!`),
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <InputNumber min={0} />
          </Form.Item>
        </Col>

        <Col xs={24} md={12} className={styles.recursiveAmount}>
          <Form.Item
            name={[...formItemName, "recurringType"]}
            initialValue={RecurringType.Month}
          >
            <Select
              showSearch
              placeholder="Week"
              optionFilterProp="label"
              className={styles.repeatSelector}
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              options={[
                { value: RecurringType.Week, label: "Week" },
                { value: RecurringType.Month, label: "Month" },
              ]}
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item className="mb-0" shouldUpdate={shouldUpdate}>
        {({ getFieldValue }) => {
          const recurringType =
            parentIndex !== undefined
              ? getFieldValue("jobLevelBillingRules")[parentIndex][fieldObj.id]
                  .recurringType
              : getFieldValue(fieldObj.id).recurringType;

          return (
            <>
            <Row gutter={[12, 0]}>
              {recurringComponent[recurringType]}
              <Col xs={24} md={12}>
                <Form.Item
                  label="Repeat Occurrences"
                  name={[...formItemName, "endAfterOccurances"]}
                  rules={[
                    {
                      required: true,
                      message: "Repeat Occurrences is required.",
                    },
                    {
                      pattern: new RegExp("^[0-9]*$", "g"),
                      message: "Decimal values are not allowed!",
                    },
                    {
                      type: "number",
                      max: 10,
                      message: "Occurrences cannot exceed 10.",
                    },
                  ]}
                >
                  <InputNumber placeholder="Repeat Occurrences" min={1} />
                </Form.Item>
              </Col>
            </Row>
            { recurringType === RecurringType.Month &&
              <RepetitiveMonthDayAlert />
            }
            </>
          );
        }}
      </Form.Item>

      <Row align="bottom" gutter={[12, 0]}>
        <Col xs={24} md={24} xl={12} className={styles.recursiveAmount}>
          <Form.Item
            className={styles.formItemRepeat}
            name={[...formItemName, "amount"]}
            label="Amount"
            rules={[{ required: true, message: "Please enter amount!" }]}
          >
            <InputNumber placeholder="Amount" min={0} />
          </Form.Item>
        </Col>

        <Col xs={24} md={12} xl={6}>
          <Form.Item name={[...formItemName, "currency"]} initialValue="USD">
            <Select
              showSearch={false}
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              placeholder="Currency"
              options={currencyList.map(({ code }) => ({
                value: code,
                label: code,
              }))}
            />
          </Form.Item>
        </Col>

        <AmountTypeField
          disabled={!fieldObj.isDependentOn}
          formItemName={[...formItemName, "amountType"]}
        />
      </Row>

      <Row align="bottom" gutter={[12, 0]}>
        <Col flex={1}>
          <Form.Item
            label="Referral Fee"
            name={[...formItemName, "referral"]}
            rules={[
              {
                required: true,
                message: "Please enter referral fee!",
              },
            ]}
          >
            <InputNumber placeholder="Referral Fee" />
          </Form.Item>
        </Col>

        <AmountTypeField
          disabled={!fieldObj.isDependentOn}
          formItemName={[...formItemName, "referralAmountType"]}
        />
      </Row>

      <Row align="bottom" gutter={[12, 0]}>
        <Col flex={1}>
          <Form.Item
            label="Rehire Fee"
            name={[...formItemName, "reHired"]}
            rules={[
              {
                required: true,
                message: "Please enter rehire fee!",
              },
            ]}
          >
            <InputNumber placeholder="Rehire Fee" />
          </Form.Item>
        </Col>

        <AmountTypeField
          disabled={!fieldObj.isDependentOn}
          formItemName={[...formItemName, "reHiredAmountType"]}
        />
      </Row>

      <Row align="bottom" gutter={[12, 0]}>
        <Col flex={1}>
          <Form.Item
            label="Internal Fee"
            name={[...formItemName, "internal"]}
            rules={[
              {
                required: true,
                message: "Please enter internal fee!",
              },
            ]}
          >
            <InputNumber placeholder="Internal" />
          </Form.Item>
        </Col>

        <AmountTypeField
          disabled={!fieldObj.isDependentOn}
          formItemName={[...formItemName, "internalAmountType"]}
        />
      </Row>

      <Row>
        <Col xs={24} md={12}>
          <Form.Item
            initialValue={false}
            valuePropName="checked"
            name={[...formItemName, "isCommissionable"]}
          >
            <Checkbox>Is Commissionable</Checkbox>
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );
}
